import { ProductCardProduct } from 'shared/types/product/Product';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useCart } from 'frontastic/hooks';
import { useMiniCart } from 'context/miniCart';
import { Variant } from 'shared/types/product';
import CrossSellModalHeader from 'components/commercetools-ui/molecules/CrossSellModalHeader';
import CrossSellModalFooter from 'components/commercetools-ui/molecules/CrossSellModalFooter';
import CrossSellModalProductItem from 'components/commercetools-ui/molecules/CrossSellModalProductItem';
import { Dialog } from '@headlessui/react';
import { cn } from 'helpers/utils/classnames';
import { gtmQuantitySelector } from 'helpers/utils/gtm-events/gtmEccomerce';
import { LineItemMapper } from 'helpers/utils/gtm-events/mapper/LineItemMapper';
import { AccountContext } from 'context/account';

interface CrossSellModalProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  minRequired: number;
  supplies: ProductCardProduct[];
  moreCompatible: {
    label: string;
    slug: string;
  };
}

const CrossSellModal = ({ isOpen, supplies, title, moreCompatible, minRequired, onClose }: CrossSellModalProps) => {
  const [quantities, setQuantities] = useState<number[]>(new Array(supplies.length).fill(minRequired));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { addItem, data: cart } = useCart();
  const { show: showMiniCart } = useMiniCart();

  const { account } = useContext(AccountContext);

  const setQuantity = (index: number, quantity: number) => {
    const newQuantities = [...quantities];
    newQuantities[index] = quantity;
    setQuantities(newQuantities);
  };

  const decreaseQuantity = (index: number) => {
    const newQuantity = Math.max(1, quantities[index] - 1);
    setQuantity(index, newQuantity);

    gtmQuantitySelector({
      clickAction: 'reduced',
      itemDataLayer: LineItemMapper.fromProductToItemDataLayer(
        supplies[index],
        newQuantity,
        supplies[index].variants[0],
      ),
      newQuantity: newQuantity,
      source: 'Cross Sell overlay',
      cart,
      account,
    });
  };

  const increaseQuantity = (index: number) => {
    const currentQuantity = quantities[index];
    setQuantity(index, currentQuantity + 1);
    gtmQuantitySelector({
      clickAction: 'increased',
      itemDataLayer: LineItemMapper.fromProductToItemDataLayer(
        supplies[index],
        currentQuantity + 1,
        supplies[index].variants[0],
      ),
      newQuantity: currentQuantity + 1,
      source: 'Cross Sell overlay',
      cart,
      account,
    });
  };

  const handleAddToCart = async (variant: Variant, quantity: number) => {
    setIsLoading(true);
    try {
      if (variant) {
        await addItem(variant, quantity);
        toast.success('Item Added');
      }
      onClose();
      showMiniCart();
    } catch (error) {
      toast.error('There was an error!');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setQuantities(
      Array.from({ length: supplies.length }, (_, index) => {
        const variant = supplies[index].variants?.[0];
        const isClearance = variant?.originalPrice && variant.price?.centAmount !== variant.originalPrice.centAmount;
        const isDiscontinued = variant?.attributes?.isDiscontinued ?? false;

        const availableQuantity = variant?.availableQuantity || 1;
        return isClearance || isDiscontinued ? availableQuantity : minRequired;
      }),
    );
  }, [supplies, minRequired]);

  return (
    <Dialog
      role="dialog"
      aria-modal="true"
      aria-labelledby="cross-sell-modal-title"
      data-testid="cross-sell-modal"
      unmount
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-50 overflow-y-auto bg-gray-600/75"
    >
      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <Dialog.Panel className="dark:bg-primary-200 absolute left-1/2 top-1/2 w-[90%] max-w-[90%] -translate-x-1/2 -translate-y-1/2 overflow-auto bg-white px-4 py-2 sm:px-6 md:w-[827px] lg:px-8">
          <div className="pb-3 pt-5 sm:pb-4 sm:pt-8" aria-labelledby="profile-modal-title">
            <CrossSellModalHeader onClose={onClose} />
            <div className="customtablescrollbar max-h-[calc(90vh_-_100px)] overflow-y-auto pr-4">
              {!!title && <p className="text-center">{title}</p>}
              <ul className={cn('grid grid-cols-2 gap-x-[22px] gap-y-[30px] md:grid-cols-3', title && 'mt-8')}>
                {supplies.map((product, index) => {
                  return (
                    <CrossSellModalProductItem
                      key={`cross-sell-product-${product.productId}`}
                      product={product}
                      quantity={quantities[index]}
                      setQuantity={(quantity) => setQuantity(index, quantity)}
                      decreaseQuantity={() => decreaseQuantity(index)}
                      increaseQuantity={() => increaseQuantity(index)}
                      handleAddToCart={(variant, quantity) => handleAddToCart(variant, quantity)}
                      isLoading={isLoading}
                    />
                  );
                })}
              </ul>
              <CrossSellModalFooter moreCompatible={moreCompatible} onClose={onClose} />
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default CrossSellModal;
